import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  Alert,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  saveCareGapWorkflow,
  saveCareGapSchedule,
  saveCareGapOutreach,
  saveCareGapSubmit,
} from "../../../../utils/services/CareGapsV2/careGapsV2Service";
import {
  OUTREACH_CHANNELS,
  OUTREACH_NEXT_ACTIONS,
  buildGroupedWorkflowItems,
  getOutreachResultsForChannel,
} from "../../common/constants";
import { getLocations } from "../../../../utils/services/control/masterDataService";

function ModalShell({ open, onClose, title, subtitle, onSubmit, submitting, submitLabel, children, error }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0 }}>
        {title}
        {subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {children}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} color="inherit">Cancel</Button>
        <Button onClick={onSubmit} disabled={submitting} variant="contained">
          {submitting ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ScheduleModal({ open, careGap, onClose, onSaved }) {
  const [scheduledDate, setScheduledDate] = useState(null);
  const [locationId, setLocationId] = useState("");
  const [note, setNote] = useState("");
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setScheduledDate(null);
    setLocationId(careGap?.locationId ?? "");
    setNote("");
    setError("");
    getLocations({ status: 1, clinical: true })
      .then((arr) => setLocations(Array.isArray(arr) ? arr : []))
      .catch(() => setLocations([]));
  }, [open, careGap]);

  const handleSubmit = async () => {
    if (!scheduledDate || !scheduledDate.isValid?.()) {
      setError("Please select a valid date.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await saveCareGapSchedule(careGap.careGapId, {
        scheduledDate: scheduledDate.format("YYYY-MM-DD"),
        locationId: locationId || null,
        note: note || undefined,
      });
      if (res?.result === 0) {
        onSaved?.(res);
        onClose?.();
      } else {
        setError(res?.message || "Could not save the appointment.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Unexpected error while saving the appointment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Schedule appointment"
      subtitle={careGap?.hedisDisplayName || careGap?.careGapName}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Save"
      error={error}
    >
      <DatePicker
        label="Appointment date"
        value={scheduledDate}
        onChange={(d) => setScheduledDate(d)}
        format="YYYY-MM-DD"
        slotProps={{ textField: { size: "small", fullWidth: true } }}
      />

      <FormControl size="small" fullWidth>
        <InputLabel>Location</InputLabel>
        <Select
          label="Location"
          value={locationId ?? ""}
          onChange={(e) => setLocationId(e.target.value === "" ? "" : Number(e.target.value))}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.locationId} value={l.locationId}>{l.medicalCenter}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        multiline
        minRows={2}
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </ModalShell>
  );
}

export function OutreachModal({ open, careGap, workflowStatuses = [], onClose, onSaved }) {
  const [workflowStatusToId, setWorkflowStatusToId] = useState(62);
  const [outreachChannel, setOutreachChannel] = useState("Phone");
  const [outreachResult, setOutreachResult] = useState("Contacted");
  const [outreachNextAction, setOutreachNextAction] = useState("Schedule appointment");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Catálogo de workflow statuses agrupado (endpoint GET /api/CareGapsV2/workflow-statuses).
  const workflowItems = useMemo(
    () => buildGroupedWorkflowItems(workflowStatuses),
    [workflowStatuses]
  );

  // Results válidos para el Channel seleccionado. Algunos canales (p.ej.
  // "Lab order/Online Order") tienen un set propio de resultados.
  const availableResults = useMemo(
    () => getOutreachResultsForChannel(outreachChannel),
    [outreachChannel]
  );

  useEffect(() => {
    if (!open) return;
    setWorkflowStatusToId(62);
    setOutreachChannel("Phone");
    setOutreachResult("Contacted");
    setOutreachNextAction("Schedule appointment");
    setNote("");
    setError("");
  }, [open]);

  // Si el Result actual no aplica al Channel seleccionado, lo reseteamos al
  // primero disponible para evitar mandar al backend un valor inválido.
  useEffect(() => {
    if (!availableResults.includes(outreachResult)) {
      setOutreachResult(availableResults[0] || "");
    }
  }, [availableResults, outreachResult]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await saveCareGapOutreach(careGap.careGapId, {
        workflowStatusToId,
        outreachChannel,
        outreachResult,
        outreachNextAction: outreachNextAction || undefined,
        note: note || undefined,
      });
      if (res?.result === 0) {
        onSaved?.(res);
        onClose?.();
      } else {
        setError(res?.message || "Could not save the outreach.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Log outreach"
      subtitle={careGap?.hedisDisplayName || careGap?.careGapName}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Save"
      error={error}
    >
      <FormControl size="small" fullWidth>
        <InputLabel>Channel</InputLabel>
        <Select label="Channel" value={outreachChannel} onChange={(e) => setOutreachChannel(e.target.value)}>
          {OUTREACH_CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel>Result</InputLabel>
        <Select label="Result" value={outreachResult} onChange={(e) => setOutreachResult(e.target.value)}>
          {availableResults.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel>Next action</InputLabel>
        <Select label="Next action" value={outreachNextAction} onChange={(e) => setOutreachNextAction(e.target.value)}>
          <MenuItem value=""><em>—</em></MenuItem>
          {OUTREACH_NEXT_ACTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel>Resulting workflow</InputLabel>
        <Select
          label="Resulting workflow"
          value={workflowStatusToId}
          onChange={(e) => setWorkflowStatusToId(Number(e.target.value))}
        >
          {workflowItems.map((entry, idx) =>
            entry.type === "header" ? (
              <ListSubheader key={`header-${idx}`}>{entry.label}</ListSubheader>
            ) : (
              <MenuItem key={entry.value} value={entry.value}>
                {entry.label}
              </MenuItem>
            )
          )}
        </Select>
      </FormControl>

      <TextField
        size="small"
        multiline
        minRows={2}
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </ModalShell>
  );
}

export function SubmitModal({ open, careGap, onClose, onSaved }) {
  const [submitTitle, setSubmitTitle] = useState("");
  const [submitNote, setSubmitNote] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const measure = careGap?.hedisDisplayName || careGap?.careGapName || "Care Gap";
    setSubmitTitle(`${measure} — Evidence`);
    setSubmitNote("");
    setNote("");
    setError("");
  }, [open, careGap]);

  const handleSubmit = async () => {
    if (!submitTitle?.trim()) {
      setError("Title is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await saveCareGapSubmit(careGap.careGapId, {
        submitTitle,
        submitNote: submitNote || undefined,
        note: note || undefined,
      });
      if (res?.result === 0) {
        onSaved?.(res);
        onClose?.();
      } else {
        setError(res?.message || "Could not save the submit.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Submit to Availity"
      subtitle={careGap?.hedisDisplayName || careGap?.careGapName}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Submit"
      error={error}
    >
      <TextField
        size="small"
        label="Title"
        value={submitTitle}
        onChange={(e) => setSubmitTitle(e.target.value)}
        required
      />
      <TextField
        size="small"
        multiline
        minRows={3}
        label="Evidence description"
        value={submitNote}
        onChange={(e) => setSubmitNote(e.target.value)}
      />
      <TextField
        size="small"
        multiline
        minRows={2}
        label="Internal note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </ModalShell>
  );
}

export function WorkflowChangeModal({ open, careGap, workflowStatuses = [], onClose, onSaved }) {
  const [workflowStatusToId, setWorkflowStatusToId] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setWorkflowStatusToId("");
    setNote("");
    setError("");
  }, [open]);

  // Dropdown agrupado al estilo del "Care Gap Actions" legacy.
  const groupedItems = useMemo(
    () => buildGroupedWorkflowItems(workflowStatuses),
    [workflowStatuses]
  );

  const handleSubmit = async () => {
    if (!workflowStatusToId) {
      setError("Please select a workflow status.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await saveCareGapWorkflow(careGap.careGapId, {
        workflowStatusToId: Number(workflowStatusToId),
        note: note || undefined,
      });
      if (res?.result === 0) {
        onSaved?.(res);
        onClose?.();
      } else {
        setError(res?.message || "Could not change the workflow.");
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Care Gap Actions"
      subtitle={`${careGap?.hedisDisplayName || careGap?.careGapName} · Current: ${careGap?.workflowStatusName || "—"}`}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel="Save"
      error={error}
    >
      <TextField
        select
        size="small"
        fullWidth
        label="New workflow"
        value={workflowStatusToId}
        onChange={(e) => setWorkflowStatusToId(e.target.value)}
      >
        {groupedItems.length === 0 && (
          <MenuItem value="" disabled>
            <em>No workflow statuses available</em>
          </MenuItem>
        )}
        {groupedItems.map((entry, idx) =>
          entry.type === "header" ? (
            <ListSubheader key={`header-${idx}`}>{entry.label}</ListSubheader>
          ) : (
            <MenuItem
              key={entry.value}
              value={entry.value}
              disabled={entry.value === careGap?.workflowStatusId}
            >
              {entry.label}
            </MenuItem>
          )
        )}
      </TextField>
      <TextField
        size="small"
        multiline
        minRows={2}
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </ModalShell>
  );
}
